import {
  Button,
  IconWithBackground,
  InternalIcon,
  Link,
  Stack,
  Typography,
  createLucideIcon,
  useTheme
} from "@carrotfertility/carotene-core"
import { ButtonSkeleton, ParagraphSkeleton, RailTile, Skeleton, TextSkeleton } from "@internal/ui"
import { Calendar } from "lucide-react"
import React from "react"
import { useIntl } from "react-intl"
import { Link as ReactRouterLink } from "react-router-dom"
import { AppointmentsAvatar } from "./AppointmentsAvatar"
import {
  formatAppointmentDateWithYear,
  formatAppointmentTimeWithZone,
  formatProviderName,
  formatStartingIn,
  isSameLocalDay
} from "./formatting"
import { useAppointments } from "./useAppointments"
import { useAppointmentTiming } from "./useAppointmentTiming"
import type { UpcomingAppointment } from "./types"

const CalendarIcon = createLucideIcon(Calendar, "CalendarIcon")

// Shared with the skeleton, which lays each out transparent so it wraps where the body does.
const NO_UPCOMING_HEADING = "No upcoming appointments"
const BOOKING_COPY =
  "Ready to talk with a specialist? Book an appointment when you’re ready to review your plan, questions, or next steps."
const BOOK_LABEL = "Book appointment"

// Carotene's `medium` `IconWithBackground`: a fixed 60px square at a 16% radius.
const BOOKING_ICON_SIZE = 60

export type HomeAppointmentsTileProps = {
  onBookAppointment?: React.MouseEventHandler<HTMLElement>
  onPrepareForVisit?: (appointmentId: string) => void
  /** Both injected by the host — the package cannot reach its `Paths` enum. */
  bookingPath: string
  appointmentsPath: string
}

function Heading(): JSX.Element {
  return (
    <Typography variant="h5" component="h2" color="text.primary">
      Appointments
    </Typography>
  )
}

function ViewAllLink({ appointmentsPath }: { appointmentsPath: string }): JSX.Element {
  return (
    <Link
      component={ReactRouterLink}
      to={appointmentsPath}
      underline="hover"
      endIcon={<InternalIcon color="primary" fontSize="small" />}
      sx={{ alignSelf: "flex-start" }}
    >
      View all appointments
    </Link>
  )
}

// The "View all" link is the only difference between the two comps that share this body: a member
// with no history has nowhere to go.
function BookingBody({
  hasHistory,
  bookingPath,
  appointmentsPath,
  onBookAppointment
}: {
  hasHistory: boolean
  bookingPath: string
  appointmentsPath: string
  onBookAppointment?: React.MouseEventHandler<HTMLElement>
}): JSX.Element {
  const theme = useTheme()
  return (
    <>
      <IconWithBackground icon={CalendarIcon} color="primary" />
      <Heading />
      <Stack spacing={theme.spacing(theme.tokens.spacing.xs)}>
        <Typography variant="h6" component="h3">
          {NO_UPCOMING_HEADING}
        </Typography>
        <Typography variant="body1">{BOOKING_COPY}</Typography>
      </Stack>
      <Button
        variant="outlined"
        color="secondary"
        component={ReactRouterLink}
        to={bookingPath}
        onClick={onBookAppointment}
        sx={{ alignSelf: "flex-start" }}
      >
        {BOOK_LABEL}
      </Button>
      {hasHistory && <ViewAllLink appointmentsPath={appointmentsPath} />}
    </>
  )
}

/**
 * Imminence outranks outstanding forms: a member ten minutes out needs to join the call, not fill in
 * a form.
 *
 * Not shared with `UpNextCard.resolveAction` — its third branch shows a disabled Join and the
 * "you'll be able to join" copy, where this one sends the member to the page. The timing the two
 * do agree on is shared: see `useAppointmentTiming`.
 */
type HomeTileAction = "join" | "completeForms" | "viewDetails"

const resolveAction = (imminent: boolean, formsComplete: boolean): HomeTileAction => {
  if (imminent) {
    return "join"
  }
  return formsComplete ? "viewDetails" : "completeForms"
}

function PrimaryAction({
  action,
  appointmentId,
  joinUrl,
  canJoin,
  bookingPath,
  appointmentsPath,
  onPrepareForVisit
}: {
  action: HomeTileAction
  appointmentId: string
  joinUrl: string
  /** False means Healthie hasn't provisioned a link yet: a disabled Join, not an anchor to nowhere. */
  canJoin: boolean
  bookingPath: string
  appointmentsPath: string
  onPrepareForVisit?: (appointmentId: string) => void
}): JSX.Element {
  const sharedProps = {
    variant: "outlined",
    color: "secondary",
    sx: { alignSelf: "flex-start" }
  } as const

  // A `switch` rather than a lookup: the `telemedicine.getAppointments` response feeds this, and
  // CodeQL's `js/unvalidated-dynamic-method-call` fires on calling a function value chosen by
  // external input.
  switch (action) {
    case "join":
      if (!canJoin) {
        return (
          <Button {...sharedProps} disabled>
            Join
          </Button>
        )
      }
      // A new tab: losing the app's state mid-navigation to join a call is the worse failure.
      return (
        <Button {...sharedProps} component="a" href={joinUrl} target="_blank" rel="noopener noreferrer">
          Join
        </Button>
      )
    case "completeForms":
      return (
        <Button
          {...sharedProps}
          component={ReactRouterLink}
          to={`${bookingPath}/${appointmentId}/forms?source=in_app`}
          onClick={() => onPrepareForVisit?.(appointmentId)}
        >
          Complete forms
        </Button>
      )
    case "viewDetails":
      return (
        <Button {...sharedProps} component={ReactRouterLink} to={appointmentsPath}>
          View details
        </Button>
      )
    default: {
      const unhandled: never = action
      throw new Error(`Unhandled home appointments tile action: ${String(unhandled)}`)
    }
  }
}

function UpcomingBody({
  appointment,
  bookingPath,
  appointmentsPath,
  onPrepareForVisit
}: {
  appointment: UpcomingAppointment
  bookingPath: string
  appointmentsPath: string
  onPrepareForVisit?: (appointmentId: string) => void
}): JSX.Element {
  const theme = useTheme()
  const intl = useIntl()
  const { now, imminent, joinUrl, canJoin } = useAppointmentTiming(appointment)

  const { id, provider, date, formsComplete } = appointment
  // A wider window than imminence: a 5pm visit reads "Today" all morning without yet offering Join.
  const dateLabel = isSameLocalDay(date, now) ? "Today" : formatAppointmentDateWithYear(intl, date)

  return (
    <>
      <Heading />
      <Stack direction="row" alignItems="center" spacing={theme.spacing(theme.tokens.spacing.sm)}>
        <AppointmentsAvatar name={provider.name} photoUrl={provider.photoUrl} />
        <Typography variant="h6" component="p" color="text.primary">
          {formatProviderName(provider)}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={theme.spacing(theme.tokens.spacing.xs)}>
        <CalendarIcon fontSize="small" aria-hidden />
        <Typography variant="body1" color="text.primary">
          {`${dateLabel} · ${formatAppointmentTimeWithZone(intl, date)}`}
        </Typography>
      </Stack>
      {imminent && (
        // `color` after `typography`, which spreads a role carrying its own.
        <Typography sx={{ typography: "chip", color: theme.palette.primary.main }}>
          {`Starting ${formatStartingIn(intl, date, now)}`}
        </Typography>
      )}
      <PrimaryAction
        action={resolveAction(imminent, formsComplete)}
        appointmentId={id}
        joinUrl={joinUrl}
        canJoin={canJoin}
        onPrepareForVisit={onPrepareForVisit}
        bookingPath={bookingPath}
        appointmentsPath={appointmentsPath}
      />
      <ViewAllLink appointmentsPath={appointmentsPath} />
    </>
  )
}

/**
 * Mirrors `BookingBody` with no history, the common case: most members have nothing booked, and
 * the rail sits beside the plan card, so a frame of another height shifts the column when the read
 * lands. A booked visit's body, or a history link, still grows the rail. No design frame exists for
 * this.
 */
export function HomeAppointmentsTileSkeleton(): JSX.Element {
  const theme = useTheme()
  return (
    <RailTile color="primary">
      {/* `lg`, the settled bodies' rhythm. */}
      <Stack role="status" aria-label="Loading your appointments" spacing={theme.spacing(theme.tokens.spacing.lg)}>
        <Skeleton width={BOOKING_ICON_SIZE} height={BOOKING_ICON_SIZE} sx={{ borderRadius: "16%" }} />
        <TextSkeleton variant="h5" width={128} />
        {/* The body's own copy wherever it wraps: the rail narrows to ~150px at 960–1199px. */}
        <Stack spacing={theme.spacing(theme.tokens.spacing.xs)}>
          <ParagraphSkeleton variant="h6" text={NO_UPCOMING_HEADING} />
          <ParagraphSkeleton variant="body1" text={BOOKING_COPY} />
        </Stack>
        <ButtonSkeleton label={BOOK_LABEL} />
      </Stack>
    </RailTile>
  )
}

/**
 * The consumer home rail's Appointments tile.
 *
 * Reads for itself rather than taking data as a prop — `useAppointments` shares a query key, so this
 * dedupes against the Appointments page's own read.
 *
 * Renders nothing on failure: falling back to the booking body would tell a member with a visit this
 * afternoon "Ready to talk with a specialist? Book…", and booking stays reachable from the nav anyway.
 */
export function HomeAppointmentsTile({
  bookingPath,
  appointmentsPath,
  onBookAppointment,
  onPrepareForVisit
}: HomeAppointmentsTileProps): JSX.Element | null {
  const { data, isLoading, isError } = useAppointments()

  if (isError) {
    return null
  }
  if (isLoading || !data) {
    return <HomeAppointmentsTileSkeleton />
  }

  // A canceled or completed record can reach the `upcoming` list; dating it and offering Join would
  // claim something untrue.
  const upcoming = data.upcoming.find((appointment) => appointment.status === "scheduled") ?? null

  return (
    <RailTile color="primary">
      {upcoming ? (
        <UpcomingBody
          appointment={upcoming}
          bookingPath={bookingPath}
          appointmentsPath={appointmentsPath}
          onPrepareForVisit={onPrepareForVisit}
        />
      ) : (
        <BookingBody
          hasHistory={data.past.length > 0}
          bookingPath={bookingPath}
          appointmentsPath={appointmentsPath}
          onBookAppointment={onBookAppointment}
        />
      )}
    </RailTile>
  )
}
